import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './PensionCalculatorPage/components/CalculationForm';
import ResultsOverview from './PensionCalculatorPage/components/ResultsOverview';
import PensionProjection from './PensionCalculatorPage/components/PensionProjection';
import QuickAdjustmentSidebar from './PensionCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './PensionCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './PensionCalculatorPage/components/TipsSidebar';
import InfoContent from './PensionCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  birthDate: new Date(new Date().getFullYear() - 30, 0, 1).toISOString().split('T')[0], // По умолчанию 30 лет
  gender: 'male', // 'male' или 'female'
  retirementAge: '', // Автоматически заполняется в useEffect
  salaryAmount: '80000', // Текущая зарплата
  workExperienceYears: '5', // Стаж работы на текущий момент
  additionalExperienceYears: '0', // Стаж, не учтённый в ПФР
  futureWorkYears: '30', // Сколько ещё планирует работать
  savingsAmount: '100000', // Накопления на данный момент
  monthlyContribution: '5000', // Ежемесячные отчисления на накопления
  annualIncreaseRate: '4', // Ежегодный рост зарплаты (%)
  investmentReturnRate: '7', // Доходность инвестиций (%)
  inflationRate: '4', // Прогнозируемая инфляция (%)
  pensionType: 'both', // 'mandatory', 'voluntary', 'both'
  voluntaryPensionType: 'npf', // 'npf', 'life_insurance', 'individual'
  npfReturnRate: '6', // Доходность НПФ
  selfInvestmentType: 'conservative', // 'conservative', 'balanced', 'aggressive'
  includeMaternityCapital: false, // Учитывать материнский капитал
  includeRegionalSupport: false, // Учитывать региональные доплаты
  region: 'moscow', // Регион для региональных доплат
  includeRuralBonus: false, // Надбавка за сельский стаж
  includeProfessionalCoefficient: false, // Профессиональные коэффициенты
  professionalCategory: 'default', // Категория профессии
};

// Определение пенсионного возраста по годам (таблица актуальна на 2025 год)
export const retirementAges = {
  male: 65, // Мужчины выходят на пенсию в 65 лет
  female: 60 // Женщины выходят на пенсию в 60 лет
};

// Профессиональные категории
export const professionalCategories = [
  { id: 'default', name: 'Общие условия', retirementBonus: 0, coefficientBonus: 1 },
  { id: 'hazardous', name: 'Опасные условия труда (список 1)', retirementBonus: 10, coefficientBonus: 1.3 },
  { id: 'difficult', name: 'Тяжелые условия труда (список 2)', retirementBonus: 5, coefficientBonus: 1.2 },
  { id: 'far_north', name: 'Работа в условиях Крайнего Севера', retirementBonus: 5, coefficientBonus: 1.5 },
  { id: 'medical', name: 'Медицинские работники', retirementBonus: 5, coefficientBonus: 1.15 },
  { id: 'educational', name: 'Педагогические работники', retirementBonus: 5, coefficientBonus: 1.1 },
  { id: 'pilot', name: 'Летный состав гражданской авиации', retirementBonus: 8, coefficientBonus: 1.4 },
  { id: 'miner', name: 'Работники угольной промышленности', retirementBonus: 7, coefficientBonus: 1.25 },
  { id: 'rescue', name: 'Спасатели МЧС', retirementBonus: 7, coefficientBonus: 1.3 },
  { id: 'artist', name: 'Творческие работники', retirementBonus: 5, coefficientBonus: 1.05 }
];

// Регионы России с минимальной пенсией
export const russianRegions = [
  { id: 'moscow', name: 'Москва', minPension: 21000 },
  { id: 'saint_petersburg', name: 'Санкт-Петербург', minPension: 17000 },
  { id: 'moscow_region', name: 'Московская область', minPension: 14000 },
  { id: 'central', name: 'Центральный ФО', minPension: 12000 },
  { id: 'northwest', name: 'Северо-Западный ФО', minPension: 12500 },
  { id: 'south', name: 'Южный ФО', minPension: 11000 },
  { id: 'north_caucasus', name: 'Северо-Кавказский ФО', minPension: 10500 },
  { id: 'volga', name: 'Приволжский ФО', minPension: 11000 },
  { id: 'ural', name: 'Уральский ФО', minPension: 12500 },
  { id: 'siberia', name: 'Сибирский ФО', minPension: 11500 },
  { id: 'far_east', name: 'Дальневосточный ФО', minPension: 13000 },
  { id: 'kaliningrad', name: 'Калининградская область', minPension: 11500 },
  { id: 'crimea', name: 'Республика Крым', minPension: 10500 }
];

// Типы инвестиционных стратегий
export const investmentStrategies = [
  { id: 'conservative', name: 'Консервативная', returnRate: 6, risk: 'Низкий' },
  { id: 'balanced', name: 'Сбалансированная', returnRate: 8, risk: 'Средний' },
  { id: 'aggressive', name: 'Агрессивная', returnRate: 12, risk: 'Высокий' }
];

/**
 * PensionCalculatorPage Component - Полный калькулятор пенсий с различными параметрами и опциями.
 */
const PensionCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('pension-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Пенсионный калькулятор | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный пенсионный калькулятор: расчет страховой и накопительной пенсии, прогноз пенсионных накоплений, моделирование добровольных отчислений в НПФ и инвестиционных стратегий с учетом текущего законодательства РФ.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [projection, setProjection] = useState([]);
  const [showFullProjection, setShowFullProjection] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedScenarios, setSavedScenarios] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных сценариев из localStorage ---
  useEffect(() => {
    const savedScenariosData = localStorage.getItem('savedPensionScenarios');
    if (savedScenariosData) {
      try {
        setSavedScenarios(JSON.parse(savedScenariosData));
      } catch (e) {
        console.error("Error loading saved scenarios:", e);
      }
    }
  }, []);

  // --- Установка пенсионного возраста в зависимости от пола ---
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      retirementAge: retirementAges[prev.gender].toString()
    }));
  }, [formData.gender]);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'insurance-pension':
          newState.pensionType = 'mandatory';
          break;
        case 'voluntary-pension':
          newState.pensionType = 'voluntary';
          break;
        case 'mixed-pension':
          newState.pensionType = 'both';
          break;
        case 'npf':
          newState.pensionType = 'voluntary';
          newState.voluntaryPensionType = 'npf';
          break;
        case 'investment':
          newState.pensionType = 'voluntary';
          newState.voluntaryPensionType = 'individual';
          break;
        case 'early-retirement':
          newState.includeProfessionalCoefficient = true;
          newState.professionalCategory = 'hazardous';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setProjection([]);
    setErrors({});
    setShowFullProjection(false);

  }, [subToolId]);

  // Сохранение сценария в localStorage
  const saveScenario = (name) => {
    if (!results) return;

    const newSavedScenario = {
      id: Date.now().toString(),
      name: name || `Сценарий пенсии от ${new Date().toLocaleDateString()}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      pensionType: formData.pensionType
    };

    const updatedSavedScenarios = [...savedScenarios, newSavedScenario];
    setSavedScenarios(updatedSavedScenarios);
    localStorage.setItem('savedPensionScenarios', JSON.stringify(updatedSavedScenarios));

    return newSavedScenario;
  };

  // Удаление сохраненного сценария
  const deleteSavedScenario = (id) => {
    const updatedSavedScenarios = savedScenarios.filter(scenario => scenario.id !== id);
    setSavedScenarios(updatedSavedScenarios);
    localStorage.setItem('savedPensionScenarios', JSON.stringify(updatedSavedScenarios));
  };

  // Загрузка сохраненного сценария
  const loadSavedScenario = (id) => {
    const scenarioToLoad = savedScenarios.find(scenario => scenario.id === id);
    if (scenarioToLoad) {
      setFormData(scenarioToLoad.formData);
      setResults(scenarioToLoad.results);
      // Пересчитаем проекцию для визуализации
      // В реальном кейсе нужно будет либо хранить проекцию в сохраненном сценарии,
      // либо заново вызвать расчетную функцию
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Пенсионный калькулятор'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              projection={projection}
              setProjection={setProjection}
              professionalCategories={professionalCategories}
              russianRegions={russianRegions}
              investmentStrategies={investmentStrategies}
              saveScenario={saveScenario}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <PensionProjection 
                  projection={projection} 
                  results={results}
                  showFullProjection={showFullProjection}
                  setShowFullProjection={setShowFullProjection}
                />
              </>
            )}

            {/* --- Информационный контент о пенсии --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения сценариев --- */}
            <ComparisonSidebar 
              savedScenarios={savedScenarios}
              deleteSavedScenario={deleteSavedScenario}
              loadSavedScenario={loadSavedScenario}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Projection Table */
        .pension-projection-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .pension-projection-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .pension-projection-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .pension-projection-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .pension-projection-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .pension-projection-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .pension-projection-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .pension-projection-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .pension-projection-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default PensionCalculatorPage;